import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';

interface BirthdayInputProps {
  value: string; // YYYY-MM-DD format
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

// 全角数字を半角に変換
const toHalfWidth = (str: string) =>
  str.replace(/[０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0));

export function BirthdayInput({ value, onChange, disabled, error }: BirthdayInputProps) {
  const theme = useTheme();

  // Parse initial value
  const [year, month, day] = value ? value.split('-') : ['', '', ''];

  const [yearValue, setYearValue] = useState(year);
  const [monthValue, setMonthValue] = useState(month);
  const [dayValue, setDayValue] = useState(day);
  const [focused, setFocused] = useState(false);

  const yearRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const dayRef = useRef<HTMLInputElement>(null);

  // handleMonthChangeで日に移動する場合、blur時のパディングをスキップするフラグ
  const skipMonthPaddingRef = useRef(false);

  const updateValue = (y: string, m: string, d: string) => {
    if (y && m && d) {
      const paddedMonth = m.padStart(2, '0');
      const paddedDay = d.padStart(2, '0');
      onChange(`${y}-${paddedMonth}-${paddedDay}`);
    } else {
      onChange('');
    }
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = toHalfWidth(e.target.value).replace(/\D/g, '').slice(0, 4);
    setYearValue(val);
    updateValue(val, monthValue, dayValue);

    // 4桁入力したら月へ移動
    if (val.length === 4) {
      monthRef.current?.focus();
    }
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = toHalfWidth(e.target.value).replace(/\D/g, '').slice(0, 2);
    const num = parseInt(val, 10);

    // 無効な値は弾く
    if (val.length === 2 && (num < 1 || num > 12)) {
      return;
    }

    setMonthValue(val);
    updateValue(yearValue, val, dayValue);

    // 2-9 は即確定して次へ
    if (val.length === 1 && num >= 2 && num <= 9) {
      const padded = val.padStart(2, '0');
      setMonthValue(padded);
      updateValue(yearValue, padded, dayValue);
      skipMonthPaddingRef.current = true;
      dayRef.current?.focus();
    }
    // 10-12 は2桁で次へ
    else if (val.length === 2 && num >= 1 && num <= 12) {
      skipMonthPaddingRef.current = true;
      dayRef.current?.focus();
    }
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = toHalfWidth(e.target.value).replace(/\D/g, '').slice(0, 2);
    const num = parseInt(val, 10);

    // 無効な値は弾く
    if (val.length === 2 && (num < 1 || num > 31)) {
      return;
    }

    setDayValue(val);
    updateValue(yearValue, monthValue, val);

    // 4-9 は即確定
    if (val.length === 1 && num >= 4 && num <= 9) {
      const padded = val.padStart(2, '0');
      setDayValue(padded);
      updateValue(yearValue, monthValue, padded);
    }
  };

  const handleFocus = () => setFocused(true);
  const handleBlur = () => setFocused(false);

  // 月のBlur時にゼロパディング（1-9のみ、0は無効なのでパディングしない）
  const handleMonthBlur = () => {
    // handleMonthChangeで日に移動した場合はパディングをスキップ
    if (skipMonthPaddingRef.current) {
      skipMonthPaddingRef.current = false;
      handleBlur();
      return;
    }

    if (monthValue && monthValue.length === 1) {
      const num = parseInt(monthValue, 10);
      if (num >= 1 && num <= 9) {
        const padded = monthValue.padStart(2, '0');
        setMonthValue(padded);
        updateValue(yearValue, padded, dayValue);
      }
    }
    handleBlur();
  };

  // 日のBlur時にゼロパディング（1-9のみ、0は無効なのでパディングしない）
  const handleDayBlur = () => {
    if (dayValue && dayValue.length === 1) {
      const num = parseInt(dayValue, 10);
      if (num >= 1 && num <= 9) {
        const padded = dayValue.padStart(2, '0');
        setDayValue(padded);
        updateValue(yearValue, monthValue, padded);
      }
    }
    handleBlur();
  };

  // MUI outlined style colors
  const borderColor = error ? theme.palette.error.main : focused ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.23)';

  const labelColor = error ? theme.palette.error.main : focused ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.6)';

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { border: 'none' },
    },
    '& .MuiInputBase-input': {
      textAlign: 'center',
      padding: '16.5px 8px',
      fontSize: '1rem',
    },
  };

  return (
    <Box sx={{ position: 'relative', mt: 1 }}>
      {/* Floating Label - MUI OutlinedInput style */}
      <Typography
        component="label"
        sx={{
          position: 'absolute',
          top: 0,
          left: 8,
          transform: 'translate(0, -50%) scale(0.75)',
          transformOrigin: 'top left',
          backgroundColor: theme.palette.background.paper,
          px: 0.5,
          fontSize: '1rem',
          color: labelColor,
          transition: 'color 0.2s ease',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        生年月日
      </Typography>

      {/* Input Container */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          border: '1px solid',
          borderColor: borderColor,
          borderWidth: focused ? 2 : 1,
          borderRadius: '4px',
          transition: 'border-color 0.2s ease, border-width 0.2s ease, box-shadow 0.2s ease',
          boxShadow: focused ? `0 0 0 3px ${theme.palette.primary.main}20` : 'none',
          backgroundColor: theme.palette.background.paper,
          '&:hover': {
            borderColor: error
              ? theme.palette.error.main
              : focused
                ? theme.palette.primary.main
                : 'rgba(0, 0, 0, 0.87)',
          },
        }}
      >
        {/* 年 */}
        <TextField
          inputRef={yearRef}
          value={yearValue}
          onChange={handleYearChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder="1990"
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              maxLength: 4,
              'aria-label': '年',
            },
          }}
          sx={{ ...textFieldSx, flex: 2 }}
        />

        <Typography color="text.secondary" sx={{ userSelect: 'none', px: 0.5 }}>
          /
        </Typography>

        {/* 月 */}
        <TextField
          inputRef={monthRef}
          value={monthValue}
          onChange={handleMonthChange}
          onFocus={handleFocus}
          onBlur={handleMonthBlur}
          disabled={disabled}
          placeholder="01"
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              maxLength: 2,
              'aria-label': '月',
            },
          }}
          sx={{ ...textFieldSx, flex: 1 }}
        />

        <Typography color="text.secondary" sx={{ userSelect: 'none', px: 0.5 }}>
          /
        </Typography>

        {/* 日 */}
        <TextField
          inputRef={dayRef}
          value={dayValue}
          onChange={handleDayChange}
          onFocus={handleFocus}
          onBlur={handleDayBlur}
          disabled={disabled}
          placeholder="15"
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              maxLength: 2,
              'aria-label': '日',
            },
          }}
          sx={{ ...textFieldSx, flex: 1 }}
        />
      </Box>
    </Box>
  );
}
